import {TemplatePortal} from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {RefModelStoreService, TakeUntilComponentDestroy} from '@q9elements/ui-kit/common';
import {find} from 'lodash';
import {debounceTime, distinctUntilChanged, filter, map, tap} from 'rxjs/operators';

import {ICustomViewFilter} from '../../../../../../models/custom-view.interface';
import {
  booleanFilterOperators,
  dateFilterOperators,
  datePickerPlaceholder,
  defaultBooleanOperator,
  defaultDateOperator,
  defaultNumberOperator,
  defaultQueryOperator,
  defaultStringOperator,
  DESCRIPTION_COLUMNS,
  GridEditTemplate,
  isExistsOptions,
  numberFilterOperators,
  queryFilterOperators,
  STARTS_WITH_MIN_CHARACTERS,
  stringFilterOperators,
  SUMMARY_COLUMNS,
  summaryDescriptionCnaFilterOperators,
  userSearchOperators
} from '../../../../dialogs/models/create-new-view.enum';
import {
  IColumn,
  IColumnField,
  IFilterOperator,
  ITemplateConfigInterface
} from '../../../../dialogs/models/create-new-view.interface';
import {ConfigGenerateService} from '../../services/config-generate.service';
import {isValidNodeAssignee} from '../../validators/node-assignee.valiator';

@Component({
  selector: 'app-edit-filter-form',
  templateUrl: './edit-filter-form.component.html',
  styleUrls: ['./edit-filter-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class EditFilterFormComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnChanges
{
  @Input() columns: IColumn[];
  @Input() filterControl: AbstractControl;
  @Input() filter: ICustomViewFilter;
  @Input() action: 'add' | 'edit';

  @ViewChild('string', {static: true}) set textTemplate(template: TemplateRef<any>) {
    this.templates[GridEditTemplate.string] = {
      template,
      operators: stringFilterOperators,
      defaultOperator: defaultStringOperator
    };
  }

  @ViewChild('integer', {static: true}) set integerTemplate(template: TemplateRef<any>) {
    this.templates[GridEditTemplate.integer] = {
      template,
      operators: numberFilterOperators,
      defaultOperator: defaultNumberOperator
    };
  }

  @ViewChild('datePicker', {static: true}) set datePickerTemplate(template: TemplateRef<any>) {
    this.templates[GridEditTemplate.date] = {
      template,
      operators: dateFilterOperators,
      defaultOperator: defaultDateOperator
    };
  }

  @ViewChild('predefined', {static: true}) set multiselectTemplate(template: TemplateRef<any>) {
    this.templates[GridEditTemplate.predefined] = {
      template,
      operators: booleanFilterOperators,
      defaultOperator: defaultBooleanOperator
    };
  }

  @ViewChild('query', {static: true}) set queryTemplate(template: TemplateRef<any>) {
    this.templates[GridEditTemplate.query] = {
      template,
      operators: queryFilterOperators,
      defaultOperator: defaultQueryOperator
    };
  }

  @ViewChild('tags', {static: true}) set tagsTemplate(template: TemplateRef<any>) {
    this.templates[GridEditTemplate.tags] = {
      template,
      operators: queryFilterOperators,
      defaultOperator: defaultQueryOperator
    };
  }

  @ViewChild('predefinedList', {static: true}) set predefinedListTemplate(
    template: TemplateRef<any>
  ) {
    this.templates[GridEditTemplate.predefinedList] = {
      template,
      operators: queryFilterOperators,
      defaultOperator: defaultQueryOperator
    };
  }

  @ViewChild('node_assignee', {static: true}) set nodeAssigneeTemplate(template: TemplateRef<any>) {
    this.templates[GridEditTemplate.node_assignee] = {
      template,
      operators: userSearchOperators,
      defaultOperator: defaultQueryOperator
    };
  }

  @ViewChild('isExistsDropdown', {static: true}) set isExistsDropdownTemplate(
    template: TemplateRef<any>
  ) {
    this.isExistsDropdownTemplateRef = template;
  }

  public operators: Array<IFilterOperator> = [];
  public selectedPortal: TemplatePortal;
  public selectedColumn: IColumn;
  public config: any = {};

  public formGroup: UntypedFormGroup = new UntypedFormGroup({
    column: new UntypedFormControl(null, Validators.required),
    operator: new UntypedFormControl(null, Validators.required),
    value: new UntypedFormControl(null),
    secondValue: new UntypedFormControl(null)
  });
  public readonly datePickerPlaceholder = datePickerPlaceholder;
  public readonly isExistsOptions = isExistsOptions;

  private readonly updateOpts = {onlySelf: false, emitEvent: true};

  private templates: ITemplateConfigInterface = {} as ITemplateConfigInterface;
  private isExistsDropdownTemplateRef: TemplateRef<any>;
  private previousOperator: string = null;
  public readonly maxTags = 10;
  public readonly tagsConfig = {
    preloadTags: true,
    hideSelected: true,
    selectOnTab: true,
    multiple: true,
    search: true,
    clearSearchOnAdd: true
  };
  public valuePlaceholder: string;

  public get teamId(): string {
    return this.rmeStore.user.userInTeam.team.id;
  }

  private get operator(): string {
    return this.formGroup.controls.operator.value;
  }

  private get value(): string {
    return this.formGroup.controls.value.value;
  }

  private get secondValue(): string {
    return this.formGroup.controls.secondValue.value;
  }

  private set operator(value) {
    if (this.filterControl?.value?.operator) {
      this.filterControl.value.operator = value;
    }

    this.formGroup.controls.operator.patchValue(value, {emitEvent: false, onlySelf: true});
  }

  constructor(
    private _viewContainerRef: ViewContainerRef,
    private rmeStore: RefModelStoreService,
    private configGenerateService: ConfigGenerateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initMainObserver();
    this.initOperatorObserver();
    this.initFilterControlObserver();

    if (!this.filter) {
      this.initColumnObserver();
    } else {
      this.initFilter();
    }

    this.initValidation();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.action === 'add' && changes['filter']?.currentValue) {
      this.initFilter();
    }
  }

  private initMainObserver(): void {
    this.formGroup.valueChanges
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((value: any) => this.updateValue(value));
  }

  private initColumnObserver(): void {
    const {column} = this.formGroup.controls;

    column.valueChanges
      .pipe(
        this.takeUntilComponentDestroy(),
        distinctUntilChanged(),
        map(columnValue => this.findColumn(columnValue)),
        filter(Boolean),
        tap(() => (this.operator = '')),
        map((selectedColumn: IColumn) => this.initTemplateForColumn(selectedColumn))
      )
      .subscribe(payload => this.formGroup.patchValue(payload, this.updateOpts));
  }

  private initOperatorObserver(): void {
    const {operator, value: valueControl} = this.formGroup.controls;

    operator.valueChanges
      .pipe(
        this.takeUntilComponentDestroy(),
        debounceTime(50),
        distinctUntilChanged(),
        tap(value => (this.operator = value))
      )
      .subscribe(operatorValue => {
        if (this.previousOperator === operatorValue) {
          return;
        }

        this.previousOperator = operatorValue;

        this.updateTemplateBasedOnOperator(operatorValue);
        this.setUpValidatorsByOperatorValue();
        valueControl.enable(this.updateOpts);
        this.setUpPlaceholder();
      });
  }

  private initFilterControlObserver() {
    this.filterControl.setValidators(this.validate.bind(this));
    this.filterControl.valueChanges
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(value => !value)
      )
      .subscribe(() => this.resetForm());
  }

  private initValidation() {
    this.filterControl.patchValue(this.filterControl.value, this.updateOpts);
  }

  private initTemplateForColumn(column: IColumn) {
    const {
      template,
      operators = [],
      defaultOperator = null
    } = this.getTemplateConfigByColumn(column.field);
    const {operator} = this.filterControl?.value || {};

    this.operators = operators;
    this.selectedColumn = column;
    this.config = this.configGenerateService.getConfig(
      column.editTemplate,
      this.filter,
      column.field.options
    );

    const selectedOperator = operator || defaultOperator?.value;
    this.updateTemplateBasedOnOperator(selectedOperator);

    this.setUpFormForColumn();
    this.setUpValidation();
    this.setUpPlaceholder();

    return {operator: selectedOperator, value: null};
  }

  private updateValue({operator, value, column, secondValue}): void {
    this.filterControl.patchValue(
      {
        operator,
        column,
        value,
        values: this.config.addedParents,
        ...(this.config.tagsList?.length && {tags: this.config.tagsList}),
        ...(this.config.predefinedList?.length && {list: this.config.predefinedList}),
        ...(operator === 'range' && {secondValue})
      },
      this.updateOpts
    );
  }

  private getTemplateConfigByColumn({
    value,
    valueType,
    queryPath
  }: Partial<IColumnField>): ITemplateConfigInterface {
    const template =
      this.templates[value] || this.templates[valueType] || this.templates[GridEditTemplate.string];

    if (this.supportsExistsOperator(value, queryPath)) {
      return {
        ...template,
        operators: summaryDescriptionCnaFilterOperators
      };
    }

    return template;
  }

  private findColumn(column: string) {
    return find(this.columns, {field: {value: column}} as IColumn);
  }

  private supportsExistsOperator(columnValue?: string, queryPath?: string): boolean {
    return (
      columnValue &&
      (SUMMARY_COLUMNS.includes(columnValue) || DESCRIPTION_COLUMNS.includes(columnValue))
    );
  }

  private updateTemplateBasedOnOperator(operatorValue: string): void {
    if (!this.selectedColumn) {
      return;
    }

    const columnValue = this.selectedColumn.field.value;
    const queryPath = this.selectedColumn.field.queryPath;

    if (this.supportsExistsOperator(columnValue, queryPath) && operatorValue === 'isExists') {
      this.selectedColumn.editTemplate = <GridEditTemplate>'isExists';
      this.selectedPortal =
        this.isExistsDropdownTemplateRef &&
        new TemplatePortal(this.isExistsDropdownTemplateRef, this._viewContainerRef);
    } else {
      const {template} = this.getTemplateConfigByColumn(this.selectedColumn.field);
      this.selectedPortal = template && new TemplatePortal(template, this._viewContainerRef);
    }
  }

  private resetForm(): void {
    this.selectedPortal = null;
    this.selectedColumn = null;
    this.previousOperator = null;
    this.formGroup.reset();
    this.config = this.configGenerateService.resetConfig();
  }

  public validate(): {formInvalid: boolean} | null {
    const isValidValues = Boolean(
      this.config.addedParents?.length ||
        this.config.tagsList?.length ||
        this.config.predefinedList?.length
    );
    const formGroupValid =
      this.operator === 'range'
        ? !this.formGroup.invalid && this.validateRange()
        : !this.formGroup.invalid;
    const isInvalid = this.config.isArrayColumns
      ? !formGroupValid || !isValidValues
      : !formGroupValid;

    return isInvalid ? {formInvalid: true} : null;
  }

  private initFilter(): void {
    const column = this.findColumn(this.filter.column);

    this.operator = this.filter.operator;
    this.initTemplateForColumn(column);
    this.formGroup.patchValue(this.filter, {emitEvent: false, onlySelf: true});
  }

  public onAddParent(event) {
    this.config.addedParents.push(event);
    this.updateParentValues();
  }

  public removeParent(item) {
    const index = this.config.addedParents.findIndex(value => value.id === item.id);
    this.config.addedParents.splice(index, 1);
    this.updateParentValues();
  }

  private updateParentValues() {
    this.updateForm('values', this.config.addedParents);
  }

  public updateTags($event) {
    this.config.tagsList = $event;
    this.updateForm('tags', this.config.tagsList);
  }

  public updateFilterValue($event) {
    this.updateForm('value', $event);
  }

  public updatePredefinedList($event) {
    this.config.predefinedList = $event.value;
    this.updateForm('list', this.config.predefinedList);
  }

  private updateForm(field, value) {
    this.filterControl.value[field] = value;
    this.formGroup.patchValue(this.filterControl.value, {emitEvent: true, onlySelf: true});
  }

  private validateRange() {
    return this.config.isDateFilter
      ? new Date(this.value).getTime() < new Date(this.secondValue).getTime()
      : Number(this.value) < Number(this.secondValue);
  }

  private setUpFormForColumn() {
    const {value, valueType} = this.selectedColumn.field;
    const options = [value, valueType];

    if (options.includes('predefinedList')) {
      this.formGroup.addControl('list', new UntypedFormControl(this.config.predefinedList));
    }

    if (!options.includes('predefinedList') && this.formGroup.get('list')) {
      this.formGroup.removeControl('list');
    }
  }

  private setUpValidation() {
    if (
      !this.config.isParentFilter &&
      !this.config.isTagsFilter &&
      !this.config.isPredefinedListFilter
    ) {
      this.setUpValidatorsByOperatorValue();
    } else {
      this.formGroup.controls.value.setValidators(null);
    }
  }

  private setUpValidatorsByOperatorValue() {
    this.formGroup.controls.value.setValidators(null);
    this.formGroup.controls.secondValue.setValidators(null);

    if (this.action === 'add') {
      this.formGroup.controls.value.updateValueAndValidity();
      this.formGroup.controls.secondValue.updateValueAndValidity();
    }

    const isRequireValueValidation =
      !this.config.isParentFilter &&
      !this.config.isTagsFilter &&
      !this.config.isPredefinedListFilter;

    if (this.operator === 'range') {
      this.formGroup.controls.secondValue.setValidators(Validators.required);
    }

    if (isRequireValueValidation) {
      this.setUpValueValidation([Validators.required]);
    }

    if (isRequireValueValidation && this.operator === 'startswith') {
      this.setUpValueValidation([
        Validators.required,
        Validators.minLength(STARTS_WITH_MIN_CHARACTERS)
      ]);
    }
  }

  private setUpValueValidation(validators: ValidatorFn[]) {
    if (this.formGroup.controls.column.value === 'node_assignee' && this.operator !== 'contains') {
      validators.push(isValidNodeAssignee());
    }

    this.formGroup.controls.value.setValidators(validators);
  }

  private setUpPlaceholder() {
    if (this.formGroup.controls.column.value === 'node_assignee' && this.operator !== 'contains') {
      this.valuePlaceholder = 'RUBIC_CUBE.TYPE_EMAIL_PLACEHOLDER';
    } else {
      this.valuePlaceholder = 'RUBIC_CUBE.TYPE_VALUE_PLACEHOLDER';
    }
  }

  public isPredefinedOptionDisabled(value: string) {
    return this.config.predefinedList.length >= 10 && !this.config.predefinedList.includes(value);
  }
}
